import { useMemo } from "react";
import { USAGE_VIEW_TYPES } from "@/constants/usage";
/** usage data into chart format */ export const useChartData = (data, filters)=>{
    const chartData = useMemo(()=>{
        if (!data) return [];
        if (filters.viewType === USAGE_VIEW_TYPES.HOURLY && "hourlyUsage" in data) {
            // Transform hourly data - create 24-hour array with 0s for missing hours
            const hourlyData = [];
            for(let hour = 0; hour < 24; hour++){
                // Filter all records for this hour and sum them (for multiple API keys)
                const hourRecords = data.hourlyUsage.filter((h)=>h.hour === hour);
                const totalForHour = hourRecords.reduce((sum, record)=>sum + record.usageValue, 0);
                hourlyData.push({
                    label: `${String(hour).padStart(2, "0")}:00`,
                    total: totalForHour,
                    unit: "Bytes"
                });
            }
            return hourlyData;
        } else if (filters.viewType === USAGE_VIEW_TYPES.DAILY && "dailyUsage" in data) {
            // Create a map of existing data for quick lookup
            const usageMap = new Map();
            if (data.dailyUsage) {
                data.dailyUsage.forEach((record)=>{
                    usageMap.set(record.date, record.totalUsageValue || 0);
                });
            }
            // Parse dates in UTC to avoid timezone shifts
            const [startYear, startMonth, startDay] = filters.startDate.split("-").map(Number);
            const [endYear, endMonth, endDay] = filters.endDate.split("-").map(Number);
            const startDate = new Date(Date.UTC(startYear, startMonth - 1, startDay));
            const endDate = new Date(Date.UTC(endYear, endMonth - 1, endDay));
            const dailyData = [];
            // Iterate through each day in the range using UTC
            const currentDate = new Date(startDate);
            while(currentDate <= endDate){
                const year = currentDate.getUTCFullYear();
                const month = currentDate.getUTCMonth();
                const day = currentDate.getUTCDate();
                const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const label = new Date(Date.UTC(year, month, day)).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    timeZone: "UTC"
                });
                dailyData.push({
                    label,
                    total: usageMap.get(dateStr) || 0,
                    unit: "Bytes",
                    date: dateStr
                });
                currentDate.setUTCDate(currentDate.getUTCDate() + 1);
            }
            return dailyData;
        }
        return [];
    }, [
        data,
        filters.viewType
    ]);
    const maxValue = useMemo(()=>Math.max(...chartData.map((d)=>d.total), 1), [
        chartData
    ]);
    const totalUsage = useMemo(()=>chartData.reduce((sum, item)=>sum + item.total, 0), [
        chartData
    ]);
    const avgUsage = useMemo(()=>chartData.length > 0 ? Math.round(totalUsage / chartData.length) : 0, [
        chartData.length,
        totalUsage
    ]);
    const unit = useMemo(()=>{
        // Try to find the unit from the chart data, default to Bytes
        const foundUnit = chartData.find((d)=>d.unit)?.unit;
        return foundUnit || "Bytes";
    }, [
        chartData
    ]);
    return {
        chartData,
        maxValue,
        totalUsage,
        avgUsage,
        unit,
        isEmpty: chartData.length === 0 || totalUsage === 0
    };
};
